import React, { useEffect, useRef, useState } from 'react';
import { Loader2, MapPin } from 'lucide-react';
import { publicSettings } from '../services/publicSettings';
import { loadGoogleMaps } from '../services/googleMapsLoader';

/**
 * LiveMap — shows store, driver (live), and destination on a real Google Map
 * when google_maps_api_key is configured. Falls back to a clean grid placeholder
 * with markers laid out heuristically when no key is available.
 */
const LiveMap = ({ vendor, driver, destination }) => {
  const ref = useRef(null);
  const mapRef = useRef(null);
  const markersRef = useRef({});
  const pathRef = useRef(null);
  const [provider, setProvider] = useState('loading');

  // Init map once
  useEffect(() => {
    let cancelled = false;
    publicSettings.get().then(async (s) => {
      const key = s?.google_maps_api_key;
      if (!key) { if (!cancelled) setProvider('fallback'); return; }
      const g = await loadGoogleMaps(key).catch(() => null);
      if (cancelled || !g) { setProvider('fallback'); return; }
      const center = vendor || destination || { lat: 40.7128, lng: -74.006 };
      mapRef.current = new g.maps.Map(ref.current, {
        center, zoom: 14, disableDefaultUI: true, zoomControl: true,
        styles: [
          { featureType: 'poi', stylers: [{ visibility: 'off' }] },
          { featureType: 'transit', stylers: [{ visibility: 'off' }] },
        ],
      });
      setProvider('google');
    });
    return () => { cancelled = true; };
    // eslint-disable-next-line
  }, []);

  // Sync markers + recenter when coordinates change
  useEffect(() => {
    if (provider !== 'google' || !mapRef.current) return;
    const g = window.google;
    const place = (key, pos, color, label) => {
      if (!pos || pos.lat == null || pos.lng == null) {
        if (markersRef.current[key]) { markersRef.current[key].setMap(null); delete markersRef.current[key]; }
        return;
      }
      if (markersRef.current[key]) {
        // Animate the driver marker smoothly between position updates so the
        // motion feels alive rather than snapping between points.
        if (key === 'driver') {
          const marker = markersRef.current[key];
          const from = marker.getPosition();
          const to = new g.maps.LatLng(pos.lat, pos.lng);
          if (from && (from.lat() !== pos.lat || from.lng() !== pos.lng)) {
            const start = performance.now();
            const dur = 900;
            const lat0 = from.lat(); const lng0 = from.lng();
            const lat1 = pos.lat;    const lng1 = pos.lng;
            const step = (t) => {
              const k = Math.min(1, (t - start) / dur);
              const ease = 1 - Math.pow(1 - k, 3);
              marker.setPosition({ lat: lat0 + (lat1 - lat0) * ease, lng: lng0 + (lng1 - lng0) * ease });
              if (k < 1) requestAnimationFrame(step);
            };
            requestAnimationFrame(step);
          } else {
            marker.setPosition(to);
          }
        } else {
          markersRef.current[key].setPosition(pos);
        }
        return;
      }
      markersRef.current[key] = new g.maps.Marker({
        position: pos, map: mapRef.current,
        label: { text: label, color: '#fff', fontWeight: '600', fontSize: '11px' },
        icon: {
          path: g.maps.SymbolPath.CIRCLE, scale: key === 'driver' ? 14 : 12, fillColor: color, fillOpacity: 1,
          strokeColor: '#fff', strokeWeight: 3,
        },
      });
    };
    place('vendor', vendor, '#0f172a', 'S');
    place('driver', driver, '#10b981', 'D');
    place('dest', destination, '#dc2626', 'You');

    // Draw a soft route line: vendor -> driver -> destination
    const pts = [vendor, driver, destination].filter((p) => p && p.lat != null);
    if (pathRef.current) pathRef.current.setMap(null);
    if (pts.length >= 2) {
      pathRef.current = new g.maps.Polyline({
        path: pts, geodesic: true,
        strokeColor: '#10b981', strokeOpacity: 0.85, strokeWeight: 3, map: mapRef.current,
      });
      const bounds = new g.maps.LatLngBounds();
      pts.forEach((p) => bounds.extend(p));
      mapRef.current.fitBounds(bounds, 64);
    }
  }, [provider, vendor?.lat, vendor?.lng, driver?.lat, driver?.lng, destination?.lat, destination?.lng]);

  return (
    <div className="relative h-72 sm:h-80 lg:h-96 w-full" data-testid="live-map">
      <div ref={ref} className="absolute inset-0" />
      {provider === 'loading' && (
        <div className="absolute inset-0 bg-slate-50 flex items-center justify-center pointer-events-none">
          <Loader2 className="h-6 w-6 animate-spin text-emerald-600" />
        </div>
      )}
      {provider === 'fallback' && (
        <FallbackMap vendor={vendor} driver={driver} destination={destination} />
      )}
    </div>
  );
};

const FallbackMap = ({ driver }) => {
  // Mock animated path — preserved as a non-broken visual when no Maps key is set
  return (
    <div className="absolute inset-0 bg-gradient-to-br from-emerald-50 via-slate-50 to-emerald-50 overflow-hidden pointer-events-none">
      <div className="absolute inset-0 opacity-60" style={{
        backgroundImage: 'linear-gradient(rgba(15,23,42,0.06) 1px, transparent 1px), linear-gradient(90deg, rgba(15,23,42,0.06) 1px, transparent 1px)',
        backgroundSize: '24px 24px',
      }} />
      <div className="absolute inset-0 flex flex-col items-center justify-center text-slate-400 gap-1">
        <MapPin className="h-7 w-7 text-emerald-500" />
        <p className="text-xs">Live map disabled — add a Google Maps API key in admin settings.</p>
        {driver?.lat != null && <p className="text-[11px] text-slate-500">Driver location tracked</p>}
      </div>
    </div>
  );
};

export default LiveMap;
